import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { APPOINTMENT_ACTIVE_STATUSES, type AppointmentStatus } from "@bookdental/schemas";

/** Status que representam consulta "viva" (não cancelada/faltou) — o que conta no painel. A lista
 *  vive em @bookdental/schemas porque a Equipe e o servidor usam a mesma régua. */
const ACTIVE_STATUSES: readonly AppointmentStatus[] = APPOINTMENT_ACTIVE_STATUSES;

export const WEEK_ABBR = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export type WeekAppointment = {
  id: string;
  patientId: string | null;
  patientName: string;
  patientPhone?: string | null;
  dentistName?: string | null;
  /** Quem atende: fora da organização ativa, só essa pessoa inicia o atendimento. */
  dentistId?: string | null;
  salaId?: string | null;
  sala?: { id: string; nome: string; cor: string | null } | null;
  notes?: string | null;
  createdAt?: string | Date;
  date: string | Date;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color: string | null;
  procedure: string;
  startedAt?: string | Date | null;
  finishedAt?: string | Date | null;
  /** Campos além de id/nome/avatar só vêm de `appointment.listByRange` (organização ativa); o
   *  agregado cross-org da conta Pessoal traz o mínimo, daí serem todos opcionais. */
  patient?: {
    id: string;
    name: string;
    avatarUrl: string | null;
    identifier?: string | null;
    birthDate?: string | Date | null;
    sex?: string | null;
    phone?: string | null;
    tags?: string[];
  } | null;
  /** MARCA explícita de "veio de outra organização" (agregado da conta Pessoal) — sempre
   *  só-leitura nesse caso, ver `AgendaScopeFilter`.
   *
   *  Existe porque `organizationId` NÃO serve de marca: `appointment.listByRange` usa `include`
   *  e devolve todos os escalares, então o compromisso da PRÓPRIA organização também vem com o
   *  campo preenchido. Tratar "tem organizationId" como "é de fora" deixava toda a agenda do
   *  Início em só-leitura, com "(undefined)" no rótulo. */
  ehDeOutraOrganizacao?: boolean;
  organizationId?: string;
  organizationName?: string;
};

/** Semana atual, domingo→sábado (mesma convenção do AgendaMiniCalendar). */
export function currentWeekRange(ref = new Date()) {
  const start = startOfWeek(ref, { weekStartsOn: 0 });
  const end = endOfWeek(ref, { weekStartsOn: 0 });
  return { start, end };
}

export function weekDays(ref = new Date()): Date[] {
  const { start, end } = currentWeekRange(ref);
  return eachDayOfInterval({ start, end });
}

export type AgendaViewMode = "day" | "week" | "month";

/** Intervalo do widget "Agenda" do painel — dia/semana/mês, a partir da data de referência. */
export function agendaViewRange(mode: AgendaViewMode, ref = new Date()) {
  if (mode === "day") return { start: startOfDay(ref), end: endOfDay(ref) };
  if (mode === "month") return { start: startOfMonth(ref), end: endOfMonth(ref) };
  return currentWeekRange(ref);
}

export function agendaViewDays(mode: AgendaViewMode, ref = new Date()): Date[] {
  const { start, end } = agendaViewRange(mode, ref);
  return eachDayOfInterval({ start, end });
}

/** Rótulo da faixa azul ‹ período › do mobile — "Agosto 2026" / "16 a 22 de ago" /
 *  "Sex, 21 de agosto". Compartilhado entre MobileAgenda (consultas) e a agenda de
 *  pedidos do Início pra garantir que as duas faixas fiquem idênticas. */
export function rotuloDoPeriodo(mode: AgendaViewMode, ref: Date): string {
  const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (mode === "month") return capitalizar(format(ref, "MMMM yyyy", { locale: ptBR }));
  if (mode === "week") {
    const s = startOfWeek(ref, { weekStartsOn: 0 });
    const e = addDays(s, 6);
    const mStart = format(s, "MMM", { locale: ptBR });
    const mEnd = format(e, "MMM", { locale: ptBR });
    if (mStart === mEnd) return `${format(s, "d")} a ${format(e, "d")} de ${mEnd}`;
    return `${format(s, "d 'de' MMM", { locale: ptBR })} a ${format(e, "d 'de' MMM", { locale: ptBR })}`;
  }
  return capitalizar(format(ref, "EEE, d 'de' MMMM", { locale: ptBR }));
}

export function isActive(a: WeekAppointment): boolean {
  return ACTIVE_STATUSES.includes(a.status);
}

/** Agrupa consultas ativas por dia (yyyy-MM-dd), ordenadas por horário. */
export function activeApptsOnDay(
  appts: WeekAppointment[],
  day: Date,
): WeekAppointment[] {
  return appts
    .filter((a) => isActive(a) && isSameDay(new Date(a.date), day))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
}

export function countToConfirm(appts: WeekAppointment[]): number {
  return appts.filter((a) => a.status === "SCHEDULED").length;
}

export type MonthGridDay = { date: Date; isCurrentMonth: boolean };

/** Grade de calendário do mês — semanas completas (dom→sáb), incluindo dias de preenchimento
 *  do mês anterior/seguinte só pra fechar as colunas visualmente (sem contagem própria). */
export function monthGridDays(ref = new Date()): MonthGridDay[] {
  const start = startOfWeek(startOfMonth(ref), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(ref), { weekStartsOn: 0 });
  return eachDayOfInterval({ start, end }).map((date) => ({
    date,
    isCurrentMonth: isSameMonth(date, ref),
  }));
}
